import api from '../config/api';

/**
 * Agencies Service
 * Handles all agency-related API calls
 */

/**
 * Get all agencies with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 10)
 * @param {string} params.search - Search query
 * @param {string} params.agency_name - Filter by agency name
 * @param {string} params.city - Filter by city
 * @param {string} params.state - Filter by state
 * @returns {Promise} API response with agencies data and pagination
 */
export const getAgencies = async (params = {}) => {
  try {
    const { page = 1, limit = 10, search, agency_name, city, state } = params;
    
    // Calculate offset for pagination
    const offset = (page - 1) * limit;
    
    // Build query parameters
    const queryParams = {
      limit,
      offset,
    };
    
    if (search) queryParams.search = search;
    if (agency_name) queryParams.agency_name = agency_name;
    if (city) queryParams.city = city;
    if (state) queryParams.state = state;
    
    const response = await api.get('/api/agencies', { params: queryParams });
    
    // API returns { success, message, data, pagination }
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch agencies. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get a single agency by ID
 * @param {number|string} id - Agency ID
 * @returns {Promise} API response with agency data
 */
export const getAgencyById = async (id) => {
  try {
    const response = await api.get(`/api/agencies/${id}`);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch agency. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new agency
 * @param {Object} agencyData - Agency data
 * @returns {Promise} API response with created agency data
 */
export const createAgency = async (agencyData) => {
  try {
    const response = await api.post('/api/agencies', agencyData);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to create agency. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update an agency
 * @param {number|string} id - Agency ID
 * @param {Object} agencyData - Updated agency data
 * @returns {Promise} API response with updated agency data
 */
export const updateAgency = async (id, agencyData) => {
  try {
    const response = await api.put(`/api/agencies/${id}`, agencyData);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update agency. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete an agency
 * @param {number|string} id - Agency ID
 * @returns {Promise} API response
 */
export const deleteAgency = async (id) => {
  try {
    const response = await api.delete(`/api/agencies/${id}`);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to delete agency. Please try again.';
    throw new Error(errorMessage);
  }
};

